import { User, Medication, Notification } from '../types';

// Mock users
export const users: User[] = [
  {
    id: '1',
    username: 'patient1',
    password: 'password123',
    name: 'Juan Dela Cruz',
    role: 'patient',
  },
  {
    id: '2',
    username: 'caregiver1',
    password: 'password123',
    name: 'Sarah Johnson',
    role: 'caregiver',
    patients: ['1', '3'],
  },
  {
    id: '3',
    username: 'patient2',
    password: 'password123',
    name: 'Michael Smith',
    role: 'patient',
  },
  {
    id: '4',
    username: 'family1',
    password: 'password123',
    name: 'Emily Doe',
    role: 'family',
    patients: ['1'],
  },
];

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to get yesterday's date in YYYY-MM-DD format
const getYesterdayDate = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

// Helper function to get tomorrow's date in YYYY-MM-DD format
const getTomorrowDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

// Mock medications
export const medications: Medication[] = [
  {
    id: '1',
    name: 'Pyrazinamide',
    dosage: '1600mg',
    time: '12:00',
    frequency: 'Daily',
    patientId: '1',
    status: 'empty'
  },
  {
    id: '2',
    name: 'Ethambutol',
    dosage: '1100mg',
    time: '12:00',
    frequency: 'Daily',
    patientId: '1',
    status: 'empty'
  },
  {
    id: '3',
    name: 'Rifampicin',
    dosage: '600mg',
    time: '08:00',
    frequency: 'Daily',
    patientId: '1',
    status: 'empty'
  },
  {
    id: '4',
    patientId: '1',
    name: 'Isoniazid',
    dosage: '300mg',
    frequency: 'Daily',
    time: '08:00',
    date: getTodayDate(),
    status: 'pending'
  },
  {
    id: '5',
    patientId: '1',
    name: 'Pyrazinamide',
    dosage: '1600mg',
    frequency: 'Daily',
    time: '12:00',
    date: getTodayDate(),
    status: 'pending'
  },
  {
    id: '6',
    patientId: '1',
    name: 'Ethambutol',
    dosage: '1100mg',
    frequency: 'Daily',
    time: '12:00',
    date: getTodayDate(),
    status: 'pending'
  },
  {
    id: '7',
    patientId: '1',
    name: 'Rifampicin',
    dosage: '600mg',
    frequency: 'Daily',
    time: '08:00',
    date: getYesterdayDate(),
    status: 'missed'
  },
  {
    id: '8',
    patientId: '1',
    name: 'Isoniazid',
    dosage: '300mg',
    frequency: 'Daily',
    time: '08:00',
    date: getYesterdayDate(),
    status: 'pending'
  },
  {
    id: '9',
    patientId: '1',
    name: 'Rifampicin',
    dosage: '600mg',
    frequency: 'Daily',
    time: '08:00',
    date: getTomorrowDate(),
    status: 'pending'
  },
  {
    id: '10',
    patientId: '3',
    name: 'Rifampicin',
    dosage: '450mg',
    frequency: 'Daily',
    time: '09:00',
    date: getTodayDate(),
    status: 'pending'
  },
  {
    id: '11',
    patientId: '3',
    name: 'Isoniazid',
    dosage: '225mg',
    frequency: 'Daily',
    time: '09:00',
    date: getTodayDate(),
    status: 'pending'
  },
  {
    id: '12',
    patientId: '3',
    name: 'Pyrazinamide',
    dosage: '1200mg',
    frequency: 'Daily',
    time: '13:00',
    date: getTodayDate(),
    status: 'pending'
  }
];

// Mock notifications
export const notifications: Notification[] = [
  {
    id: '1',
    medicationId: '3',
    patientId: '1',
    title: 'Medication Reminder',
    message: 'Time to take your Pyrazinamide (1600mg)',
    time: '12:00',
    date: getTodayDate(),
    isRead: false,
    type: 'reminder',
  },
  {
    id: '2',
    medicationId: '4',
    patientId: '1',
    title: 'Medication Reminder',
    message: 'Time to take your Ethambutol (1100mg)',
    time: '12:00',
    date: getTodayDate(),
    isRead: false,
    type: 'reminder',
  },
  {
    id: '3',
    medicationId: '5',
    patientId: '1',
    title: 'Missed Medication',
    message: 'You missed your Rifampicin (600mg) dose',
    time: '20:00',
    date: getYesterdayDate(),
    isRead: true,
    type: 'missed',
  },
  {
    id: '4',
    medicationId: '10',
    patientId: '3',
    title: 'Medication Reminder',
    message: 'Time to take your Pyrazinamide (1200mg)',
    time: '13:00',
    date: getTodayDate(),
    isRead: false,
    type: 'reminder',
  },
];

// Define treatment types
export const treatmentTypes = ["Type A", "Type B", "Type C"];

// IMPORTANT: Patient data with location groupings to create heat map concentrations
// Distribution: 6-5-4-3-2-1 (patients per location)
export const patientData = [
  // 6 patients in Quezon City (very high concentration - will appear red)
  {
    id: "1",
    name: "Juan Dela Cruz",
    treatmentType: "Type A",
    adherenceRate: 95,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 0,
    startDate: "2023-03-15",
    nextAppointment: "2023-08-15",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 45,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "010203040506",
    phoneNumber: "09123456789"
  },
  {
    id: "2",
    name: "Maria Santos",
    treatmentType: "Type B",
    adherenceRate: 88,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-02-20",
    nextAppointment: "2023-07-20",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 38,
    sex: "Female",
    civilStatus: "Married",
    philHealthNumber: "020304050607",
    phoneNumber: "09234567890"
  },
  {
    id: "3",
    name: "Pedro Reyes",
    treatmentType: "Type C",
    adherenceRate: 92,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-04-05",
    nextAppointment: "2023-09-05",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 52,
    sex: "Male",
    civilStatus: "Widowed",
    philHealthNumber: "030405060708",
    phoneNumber: "09345678901"
  },
  {
    id: "4",
    name: "Ana Gomez",
    treatmentType: "Type A",
    adherenceRate: 90,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-01-25",
    nextAppointment: "2023-06-25",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 29,
    sex: "Female",
    civilStatus: "Single",
    philHealthNumber: "040506070809",
    phoneNumber: "09456789012"
  },
  {
    id: "5",
    name: "Ricardo Lim",
    treatmentType: "Type B",
    adherenceRate: 85,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-02-10",
    nextAppointment: "2023-07-10",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 64,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "050607080910",
    phoneNumber: "09567890123"
  },
  {
    id: "6",
    name: "Sofia Mendoza",
    treatmentType: "Type C",
    adherenceRate: 98,
    missedDoses: 0,
    confirmedDoses: 20,
    pendingDoses: 0,
    startDate: "2023-03-30",
    nextAppointment: "2023-08-30",
    municipality: "Quezon City",
    province: "Metro Manila",
    age: 41,
    sex: "Female",
    civilStatus: "Divorced",
    philHealthNumber: "060708091011",
    phoneNumber: "09678901234"
  },
  
  // 5 patients in Manila (high concentration)
  {
    id: "7",
    name: "Jose Rizal",
    treatmentType: "Type A",
    adherenceRate: 92,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-02-15",
    nextAppointment: "2023-07-15",
    municipality: "Manila",
    province: "Metro Manila",
    age: 35,
    sex: "Male",
    civilStatus: "Single",
    philHealthNumber: "070809101112",
    phoneNumber: "09789012345"
  },
  {
    id: "8",
    name: "Andres Bonifacio",
    treatmentType: "Type B",
    adherenceRate: 85,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-03-10",
    nextAppointment: "2023-08-10",
    municipality: "Manila",
    province: "Metro Manila",
    age: 42,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "080910111213",
    phoneNumber: "09890123456"
  },
  {
    id: "9",
    name: "Gabriela Silang",
    treatmentType: "Type C",
    adherenceRate: 95,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 0,
    startDate: "2023-04-20",
    nextAppointment: "2023-09-20",
    municipality: "Manila",
    province: "Metro Manila",
    age: 28,
    sex: "Female",
    civilStatus: "Single",
    philHealthNumber: "091011121314",
    phoneNumber: "09901234567"
  },
  {
    id: "10",
    name: "Emilio Aguinaldo",
    treatmentType: "Type A",
    adherenceRate: 80,
    missedDoses: 4,
    confirmedDoses: 16,
    pendingDoses: 0,
    startDate: "2023-01-05",
    nextAppointment: "2023-06-05",
    municipality: "Manila",
    province: "Metro Manila",
    age: 55,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "101112131415",
    phoneNumber: "09012345678"
  },
  {
    id: "11",
    name: "Apolinario Mabini",
    treatmentType: "Type B",
    adherenceRate: 90,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-03-25",
    nextAppointment: "2023-08-25",
    municipality: "Manila",
    province: "Metro Manila",
    age: 37,
    sex: "Male",
    civilStatus: "Single",
    philHealthNumber: "111213141516",
    phoneNumber: "09123456780"
  },
  
  // 4 patients in Cebu City (medium concentration)
  {
    id: "12",
    name: "Antonio Luna",
    treatmentType: "Type C",
    adherenceRate: 88,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-02-28",
    nextAppointment: "2023-07-28",
    municipality: "Cebu City",
    province: "Cebu",
    age: 47,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "121314151617",
    phoneNumber: "09234567891"
  },
  {
    id: "13",
    name: "Melchora Aquino",
    treatmentType: "Type A",
    adherenceRate: 95,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 0,
    startDate: "2023-01-15",
    nextAppointment: "2023-06-15",
    municipality: "Cebu City",
    province: "Cebu",
    age: 63,
    sex: "Female",
    civilStatus: "Widowed",
    philHealthNumber: "131415161718",
    phoneNumber: "09345678902"
  },
  {
    id: "14",
    name: "Diego Silang",
    treatmentType: "Type B",
    adherenceRate: 82,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-04-10",
    nextAppointment: "2023-09-10",
    municipality: "Cebu City",
    province: "Cebu",
    age: 39,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "141516171819",
    phoneNumber: "09456789013"
  },
  {
    id: "15",
    name: "Marcelo H. del Pilar",
    treatmentType: "Type C",
    adherenceRate: 90,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-03-20",
    nextAppointment: "2023-08-20",
    municipality: "Cebu City",
    province: "Cebu",
    age: 45,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "151617181920",
    phoneNumber: "09567890124"
  },
  
  // 3 patients in Davao City (medium-low concentration)
  {
    id: "16",
    name: "Gregorio del Pilar",
    treatmentType: "Type A",
    adherenceRate: 95,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 0,
    startDate: "2023-02-05",
    nextAppointment: "2023-07-05",
    municipality: "Davao City",
    province: "Davao del Sur",
    age: 31,
    sex: "Male",
    civilStatus: "Single",
    philHealthNumber: "161718192021",
    phoneNumber: "09678901235"
  },
  {
    id: "17",
    name: "Mariano Gomez",
    treatmentType: "Type B",
    adherenceRate: 87,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-04-15",
    nextAppointment: "2023-09-15",
    municipality: "Davao City",
    province: "Davao del Sur",
    age: 53,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "171819202122",
    phoneNumber: "09789012346"
  },
  {
    id: "18",
    name: "Jose Burgos",
    treatmentType: "Type C",
    adherenceRate: 92,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-01-20",
    nextAppointment: "2023-06-20",
    municipality: "Davao City",
    province: "Davao del Sur",
    age: 48,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "181920212223",
    phoneNumber: "09890123457"
  },
  
  // 2 patients in Iloilo City (low concentration)
  {
    id: "19",
    name: "Lapu-Lapu",
    treatmentType: "Type A",
    adherenceRate: 100,
    missedDoses: 0,
    confirmedDoses: 20,
    pendingDoses: 0,
    startDate: "2023-03-05",
    nextAppointment: "2023-08-05",
    municipality: "Iloilo City",
    province: "Iloilo",
    age: 36,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "192021222324",
    phoneNumber: "09901234568"
  },
  {
    id: "20",
    name: "Sergio Osmeña",
    treatmentType: "Type B",
    adherenceRate: 85,
    missedDoses: 3,
    confirmedDoses: 17,
    pendingDoses: 0,
    startDate: "2023-02-10",
    nextAppointment: "2023-07-10",
    municipality: "Iloilo City",
    province: "Iloilo",
    age: 59,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "202122232425",
    phoneNumber: "09012345679"
  },
  
  // Single cases in different locations
  {
    id: "21",
    name: "Manuel Quezon",
    treatmentType: "Type C",
    adherenceRate: 92,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 2,
    startDate: "2023-03-25",
    nextAppointment: "2023-08-25",
    municipality: "Baguio City",
    province: "Benguet",
    age: 50,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "212223242526",
    phoneNumber: "09123456781"
  },
  {
    id: "22",
    name: "Carlos P. Garcia",
    treatmentType: "Type A",
    adherenceRate: 90,
    missedDoses: 2,
    confirmedDoses: 18,
    pendingDoses: 0,
    startDate: "2023-04-01",
    nextAppointment: "2023-09-01",
    municipality: "Bacolod City",
    province: "Negros Occidental",
    age: 42,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "222324252627",
    phoneNumber: "09234567892"
  },
  {
    id: "23",
    name: "Diosdado Macapagal",
    treatmentType: "Type B",
    adherenceRate: 95,
    missedDoses: 1,
    confirmedDoses: 19,
    pendingDoses: 0,
    startDate: "2023-02-15",
    nextAppointment: "2023-07-15",
    municipality: "Cagayan de Oro",
    province: "Misamis Oriental",
    age: 57,
    sex: "Male",
    civilStatus: "Married",
    philHealthNumber: "232425262728",
    phoneNumber: "09345678903"
  }
]; 